import Link from 'next/link';
import { memo, useCallback } from 'react';

import { cn } from '@air/utils-tailwind';

import { type PublicShowcaseNavEntry } from '~/components/v2/components/showcase/publicShowcaseNavEntries';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export type PublicShowcaseNavLinkProps = {
  entry: PublicShowcaseNavEntry;
};

/**
 * One destination in the sticky public nav bar. Internal entries navigate shallowly: the link's
 * root page and every sub-page are the same Next page rendered from the same static props, so a
 * shallow push swaps the rendered page without refetching the share link or remounting the tree.
 *
 * `--headline-font-family` only exists on the public collection page root when the link customizes
 * its headline font, so the `inherit` fallback keeps an un-customized link's chrome identical.
 */
export const PublicShowcaseNavLink = memo(({ entry }: PublicShowcaseNavLinkProps) => {
  const { getPageNavigationHref, onSelectPage } = usePublicShowcaseContext();
  const { areExternalLinksClickable } = useExternalLinksClickableContext();

  const className = cn(
    // -mb-px pulls the underline onto the row's bottom border so the active indicator sits ON the
    // rule rather than above it.
    'relative -mb-px inline-flex h-11 shrink-0 items-center whitespace-nowrap border-b-2 font-[family-name:var(--headline-font-family,inherit)] text-14 no-underline transition-[color,border-color] hover:no-underline focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-8',
    entry.isActive
      ? 'border-b-gray-12 font-semibold text-gray-12'
      : 'border-b-transparent font-medium text-gray-10 hover:border-b-gray-6 hover:text-gray-12',
  );

  const handleSelectPage = useCallback(() => {
    onSelectPage(entry.pageId === null || entry.slug === null ? null : { pageId: entry.pageId, pageSlug: entry.slug });
  }, [entry.pageId, entry.slug, onSelectPage]);

  // External links always open their URL in a new tab on every surface. The navigation contract
  // can't represent an off-site destination, and opening one is harmless inside the Edit tab.
  // Free share links strip the href so authored nav URLs cannot be used as phishing bait.
  if (entry.isExternal) {
    if (!areExternalLinksClickable) {
      return (
        <span className={className} data-testid="PUBLIC_COLLECTION_NAV_LINK">
          {entry.label}
        </span>
      );
    }

    return (
      <a
        className={className}
        data-testid="PUBLIC_COLLECTION_NAV_LINK"
        href={entry.externalHref}
        rel="noopener noreferrer"
        target="_blank"
      >
        {entry.label}
      </a>
    );
  }

  const href = getPageNavigationHref?.({ pageSlug: entry.slug });

  // No href means this surface's navigation has no URLs (the collection link editor's Edit tab):
  // same look and same active semantics (aria-current), but the click drives the navigation
  // contract rather than routing — a real link would eject the author out of the editor.
  if (href === undefined) {
    return (
      <button
        aria-current={entry.isActive ? 'page' : undefined}
        className={className}
        data-testid="PUBLIC_COLLECTION_NAV_LINK"
        onClick={handleSelectPage}
        type="button"
      >
        {entry.label}
      </button>
    );
  }

  return (
    <Link
      aria-current={entry.isActive ? 'page' : undefined}
      className={className}
      data-testid="PUBLIC_COLLECTION_NAV_LINK"
      href={href}
      shallow
    >
      {entry.label}
    </Link>
  );
});

PublicShowcaseNavLink.displayName = 'PublicShowcaseNavLink';
